import { z } from "zod/v4";

// ===========================================
// Backend Environment Schema
// ===========================================
const serverEnvSchema = z.object({
	NODE_ENV: z.enum(["development", "production", "test"]).default("development"),
	PORT: z.string().default("4000").transform(Number).pipe(z.number().min(1).max(65535)),
	HOST: z.string().default("0.0.0.0"),

	// Database
	DATABASE_URL: z.url(),

	// Redis
	REDIS_URL: z.url(),

	// S3 / MinIO
	S3_ENDPOINT: z.url(),
	S3_ACCESS_KEY: z.string().min(1),
	S3_SECRET_KEY: z.string().min(1),

	/**
	 * Fayl saqlagich drayveri (spec §5.2).
	 *
	 * ⚠️ Standart `local` — mavjud xatti-harakat (`./storage` daraxti),
	 * ya'ni kalitni qo'ymagan muhit hech narsa sezmaydi. Produksiyada
	 * `s3`: fayl turi (`FileKind`) bucket'ni tanlaydi.
	 *
	 * ⚠️ Standart qiymati BOR, ya'ni `env-guard.test.ts` uni majburiy
	 * deb hisoblamaydi — kalit compose/CI/shablonlarga baribir
	 * qo'shiladi, lekin buni test EMAS, konvensiya talab qiladi.
	 */
	STORAGE_DRIVER: z.enum(["local", "s3"]).default("local"),

	// Face AI — tashqi yuz tanish servisi (spec 1b-2 §3.1).
	// Standart qiymatsiz, ya'ni MAJBURIY: bo'sh yoki noto'g'ri manzil bilan
	// ishga tushgan jarayon xatoni faqat birinchi enrollment'da ko'rsatardi.
	FACE_AI_URL: z.url(),
	FACE_AI_API_KEY: z.string().min(1),

	// JWT
	JWT_SECRET: z.string().min(32),
	JWT_EXPIRES_IN: z.string().default("15m"),
	REFRESH_TOKEN_EXPIRES_IN: z.string().default("7d"),
	// Mobil oflayn davrdan sezilarli uzun bo'lishi shart (spec §7.3): xodim
	// soatlab yoki kunlab oflayn qoladi, fon jarayoni esa 401 kelganda
	// interaktiv qayta kirish so'ray olmaydi.
	// 30–90 kun oralig'ining o'rtasi; yakuniy qiymat buyurtmachi bilan
	// kelishilishi kerak — hozircha bu BIZNING standartimiz.
	REFRESH_TOKEN_EXPIRES_IN_MOBILE: z.string().default("60d"),

	// Auth
	BCRYPT_ROUNDS: z.string().default("12").transform(Number).pipe(z.number().min(1).max(20)),

	/**
	 * Refresh cookie'ning `Secure` bayrog'i.
	 *
	 * ⚠️ Standart — `NODE_ENV === "production"`. Bu kalit AYNAN bitta
	 * holat uchun: `NODE_ENV=production` bilan, lekin TLS'siz ishlaydigan
	 * muhit (LAN staging). U yerda `Secure` cookie'ni brauzer JIMGINA
	 * tashlab yuboradi — refresh ishlamaydi, login halqaga tushadi va
	 * server loglari toza qoladi.
	 *
	 * ⚠️ `.optional()` — ya'ni `env-guard.test.ts` uni majburiy deb
	 * hisoblamaydi. TLS bor prod'da qo'yilmaydi.
	 *
	 * ⚠️ Enum'dan OLDIN bo'sh/probel satr `undefined`ga aylantiriladi —
	 * xuddi pastdagi `FCM_SERVICE_ACCOUNT_JSON`/`TELEGRAM_BOT_TOKEN` va
	 * `VITE_MAP_FALLBACK_URL` naqshi kabi (`${VAR:-}` tuzog'i).
	 * `docker-compose.yml` `COOKIE_SECURE: ${COOKIE_SECURE:-}` yozadi —
	 * operator qiymat bermasa konteynerga BO'SH SATR keladi, `undefined`
	 * EMAS. Sof `z.enum(["true","false"]).optional()` faqat `undefined`ni
	 * qabul qiladi va bo'sh satrni rad etardi — ya'ni bazaviy
	 * `docker-compose.yml` STANDART yo'lda ham `getServerEnv()` backend
	 * ishga tushishida throw qilardi (staging escape faqat tasodifan
	 * ishlagan, chunki `.env.staging.example` `COOKIE_SECURE=false`ni
	 * ANIQ beradi).
	 */
	COOKIE_SECURE: z
		.string()
		.optional()
		.transform((value) => (value && value.trim().length > 0 ? value.trim() : undefined))
		.pipe(z.enum(["true", "false"]).optional())
		.transform((value) => (value === undefined ? undefined : value === "true")),

	// CORS — vergul bilan ajratilgan origin ro'yxati
	CORS_ORIGINS: z
		.string()
		.default("http://localhost:3000")
		.transform((value) =>
			value
				.split(",")
				.map((origin) => origin.trim())
				.filter(Boolean)
		)
		.pipe(z.array(z.url()).min(1)),

	/**
	 * Backend'ning OLDIDA turgan ishonchli proksilarning IP manzillari —
	 * vergul bilan ajratilgan.
	 *
	 * ⚠️ **Domen rejimida zanjir ikki bo'g'inli:** `Traefik (192.168.3.247)
	 * → Caddy → backend`. Caddy `X-Forwarded-For` ga o'z peer'ini QO'SHADI,
	 * ya'ni backend `<haqiqiy klient>, 192.168.3.247` ni ko'radi. Bu ro'yxat
	 * `extractClientIp` ga (`apps/backend/src/lib/request-ip.ts`) qaysi
	 * elementlarni o'ngdan tashlashni aytadi.
	 *
	 * ⚠️ Standart BO'SH — bitta proksili muhit (dev, LAN staging) hech
	 * qanday o'zgarishsiz ishlaydi. Qiymat berilmasa xatti-harakat eski
	 * "oxirgi element" qoidasidek qoladi.
	 *
	 * ⚠️ Bu yerga FAQAT o'zimiz boshqaradigan proksining manzili yoziladi.
	 * Ortiqcha manzil qo'shilsa, o'sha manzildan kelgan so'rovda klient
	 * `X-Forwarded-For` ni o'zi to'ldirib, audit yozuviga soxta IP muhrlay
	 * olardi.
	 */
	TRUSTED_PROXY_IPS: z
		.string()
		.default("")
		.transform((value) =>
			value
				.split(",")
				.map((ip) => ip.trim())
				.filter(Boolean)
		)
		.pipe(z.array(z.string().min(1))),

	// Rate limiting
	RATE_LIMIT_LOGIN_MAX: z.string().default("10").transform(Number).pipe(z.number().int().min(1)),
	RATE_LIMIT_LOGIN_WINDOW: z
		.string()
		.default("900")
		.transform(Number)
		.pipe(z.number().int().min(1)),

	// Qurilmalar — TT §4.3: «bir xodim nechta qurilmadan kira olishi sozlanadi»
	MAX_DEVICES_PER_EMPLOYEE: z.string().default("1").transform(Number).pipe(z.number().int().min(1)),

	// Rozilik matnining JORIY versiyasi (spec §5.8).
	//
	// ⚠️ Bu qiymat huquqiy dalil yozuvini chegaralaydi: `consents.version`
	// mijoz tanlaydigan maydon edi va xodim `{ "version": 999 }` yuborib,
	// mavjud bo'lmagan hujjat versiyasini "qabul qildim" degan yozuv yarata
	// olardi. `findLatest` esa `desc(version)` bo'yicha tartiblaydi, ya'ni
	// bunday yozuv 2-bosqich tayanadigan "joriy rozilik holati" so'rovini ham
	// buzardi.
	//
	// Rozilik MATNINING o'zi hali buyurtmachi bilan ochiq savol (spec §12/Q8)
	// — shu sababli versiyalar katalogi hozircha DB da emas, bitta env
	// qiymatida. Matn kelishilgach, bu qiymat `consent_documents` jadvaliga
	// ko'chiriladi.
	CURRENT_CONSENT_VERSION: z.string().default("1").transform(Number).pipe(z.number().int().min(1)),

	// ===========================================
	// 2-bosqich — bildirishnoma kanallari va sync (spec §7.4, §4.1)
	// ===========================================

	/**
	 * Yoqilgan kanallar — vergul bilan ajratilgan.
	 *
	 * ⚠️ Standart `"panel"`: dev va CI da HECH QANDAY tashqi chaqiruv
	 * yuborilmaydi. Ro'yxatdan chiqarilgan kanal D blokida
	 * `createDisabledChannel()` bilan almashtiriladi — job yiqilmaydi,
	 * xabar jurnalda qoladi.
	 *
	 * ⚠️ Bo'sh ro'yxat RUXSAT ETILADI («hamma tashqi kanal o'chirilgan»)
	 * — `.min(1)` qo'ymang.
	 */
	NOTIFY_CHANNELS_ENABLED: z
		.string()
		.default("panel")
		.transform((value) =>
			value
				.split(",")
				.map((k) => k.trim())
				.filter(Boolean)
		)
		.pipe(z.array(z.enum(["push", "telegram", "panel"]))),

	/**
	 * FCM service account JSON (HTTP v1). Ixtiyoriy — yo'q bo'lsa
	 * `push` kanali o'chirilgan holatda ishlaydi.
	 *
	 * ⚠️ `.transform` bo'sh satrni `undefined` ga aylantiradi:
	 * `docker-compose.yml` `${FCM_SERVICE_ACCOUNT_JSON:-}` yozadi va
	 * operator qiymat bermasa konteynerga BO'SH SATR keladi. Usiz
	 * D bloki `JSON.parse("")` da yiqilardi.
	 */
	FCM_SERVICE_ACCOUNT_JSON: z
		.string()
		.optional()
		.transform((value) => (value && value.trim().length > 0 ? value : undefined)),

	/** Telegram Bot API tokeni. Ixtiyoriy — yuqoridagi bilan bir xil sabab. */
	TELEGRAM_BOT_TOKEN: z
		.string()
		.optional()
		.transform((value) => (value && value.trim().length > 0 ? value : undefined)),

	/**
	 * `POST /sync/locations` tanasining maksimal hajmi (bayt).
	 *
	 * ⚠️ Bu `sync_max_points_per_batch` sozlamasidan BOSHQA qorovul va
	 * u ATAYLAB env'da: nuqta sonini sanash uchun tanani parslash kerak,
	 * ya'ni bayt chegarasi parslashdan OLDIN, HTTP qatlamida ishlashi
	 * shart. 2 MiB ≈ 500 nuqta × ~200 bayt, katta zaxira bilan.
	 */
	SYNC_MAX_BODY_BYTES: z
		.string()
		.default("2097152")
		.transform(Number)
		.pipe(z.number().int().min(1)),
});

export type ServerEnv = z.infer<typeof serverEnvSchema>;

let cachedServerEnv: ServerEnv | null = null;

export function getServerEnv(): ServerEnv {
	if (cachedServerEnv) {
		return cachedServerEnv;
	}

	const result = serverEnvSchema.safeParse(process.env);

	if (!result.success) {
		const formatted = z.prettifyError(result.error);
		throw new Error(`Invalid server environment variables:\n${formatted}`);
	}

	cachedServerEnv = result.data;
	return cachedServerEnv;
}

// ===========================================
// Frontend Environment Schema (Vite)
// ===========================================

/**
 * Xavfsiz nisbiy yo'lni tekshiradi — `/` bilan boshlanadi VA `new URL()`
 * bazaviy origin'ni O'ZGARTIRMAYDI.
 *
 * ⚠️ Faqat `/^\/(?![/\\])/` regex YETARLI EMAS: u birinchi belgidan
 * keyingi `/` va `\` ni taqiqlaydi, lekin boshqa nazorat belgilarini
 * (`\t`, `\n`, `\r\n`) o'tkazib yuboradi. WHATWG URL parseri ularni
 * qiymatdan AVVAL olib tashlaydi, ya'ni `"/\t/evil.uz/x.pmtiles"`
 * brauzerda `"//evil.uz/x.pmtiles"` — protokolsiz BOSHQA host bo'lib hal
 * bo'ladi (`//host/x` bilan bir xil tuzoq, faqat maskalangan). Yagona
 * ishonchli tekshiruv — natijaviy `origin` ni bazaviy `origin` bilan
 * solishtirish, chunki `new URL()` xuddi shu qoidalar bilan hal qiladi.
 */
function nisbiyYolXavfsiz(value: string): boolean {
	if (!value.startsWith("/")) {
		return false;
	}

	try {
		return new URL(value, "http://p.invalid").origin === "http://p.invalid";
	} catch {
		return false;
	}
}

const clientEnvSchema = z.object({
	VITE_API_URL: z.url(),
	// XYZ raster tile shabloni — `{z}/{x}/{y}` o'rinbosarlari bilan
	// (`https://tile.openstreetmap.org/{z}/{x}/{y}.png`).
	//
	// ⚠️ 26.08.2026 gacha bu yerda o'z pmtiles arxivining manzili turardi
	// (1c spec §12.5, Q10). Qaror buyurtmachi tanlovi bilan qayta ko'rildi
	// — sabab va narxi `docs/infra/README.md` da.
	//
	// Standart qiymat ATAYLAB yo'q: noto'g'ri yoki bo'sh manzil bilan xarita
	// jimgina bo'sh fon bo'lib ochilardi va admin hududni "chizib" saqlab
	// ham qo'yardi.
	VITE_MAP_TILES_URL: z.url(),
	// Zaxira fon — lokal pmtiles arxivi (`tiles/uzbekistan.pmtiles`).
	// `BasemapLayer` unga FAQAT tashqi tile'lar yiqilganda o'tadi, ya'ni
	// perimetr yopiq bo'lsa xarita bo'sh qolmaydi (z15 gacha).
	//
	// ⚠️ 10.09.2026 dan STANDART YOQILGAN (spec §6.2): `apps/frontend/Dockerfile`
	// `ARG VITE_MAP_FALLBACK_URL=/tiles/uzbekistan.pmtiles`. Nisbiy yo'l
	// qabul qilinadi — `pmtiles` `fetch(this.url)` qiladi, ya'ni manzil
	// sahifa origin'iga nisbatan hal bo'ladi va host'ga bog'lanmaydi
	// (dev'da Vite plagini, prod'da Caddy `/tiles/*` ni bir xil originda beradi).
	//
	// ⚠️ `.transform` bo'sh/probel satrni `undefined` ga aylantiradi — xuddi
	// pastdagi `FCM_SERVICE_ACCOUNT_JSON`/`TELEGRAM_BOT_TOKEN` naqshi kabi.
	// Vite `--build-arg VITE_MAP_FALLBACK_URL=` bilan bundle'ga aynan BO'SH
	// SATR yozadi (`undefined` emas); `.optional()` yolg'iz uni tutmaydi va
	// `getClientEnv()` modul yuklanishida (`app/api/client.ts`) throw qilib
	// butun panelni oq ekranga aylantirardi. Bo'sh qiymat — zaxirani ATAYLAB
	// o'chirish yo'li bo'lib qoladi.
	//
	// ⚠️ Mutlaq tarmoq `http`/`https` bilan CHEKLANADI (`z.url()` yolg'iz
	// o'zi `javascript:`/`file:` kabi har qanday protokolni qabul qiladi —
	// bu maydon Leaflet qatlamiga `url` sifatida uzatiladi, ya'ni tor
	// protokol ro'yxati ikkinchi himoya qatlami).
	VITE_MAP_FALLBACK_URL: z
		.string()
		.optional()
		.transform((value) => (value && value.trim().length > 0 ? value : undefined))
		.pipe(
			z.union([z.url({ protocol: /^https?$/ }), z.string().refine(nisbiyYolXavfsiz)]).optional()
		),

	/**
	 * Telegram bot username — `NotificationChannelsPage` tepasida havola
	 * sifatida ko'rsatiladi (P1 spec §7): `@<username>` va `t.me/<username>`.
	 *
	 * ⚠️ **ENDI FAQAT `source === "none"` zaxirasi** (`NotificationChannelsPage.tsx`ning
	 * `envBotUsername()` izohiga qarang) — token DB orqali sozlangan
	 * (`source: "db"`/`"env"`) bo'lsa bu o'zgaruvchi UMUMAN o'qilmaydi:
	 * manzil endi tokendan Telegram'ning `getMe` metodi orqali avtomatik
	 * aniqlanadi va tasdiqlanadi. Bu o'zgaruvchi FAQAT token umuman
	 * sozlanmagan (eng birinchi, token-rotatsiya funksiyasidan OLDINGI)
	 * holatda ko'rinishni saqlab qolish uchun qoldirilgan — Dockerfile/
	 * compose'dan olib tashlash infratuzilma o'zgarishi, shoshilinch emas.
	 *
	 * ⚠️ Ixtiyoriy — bot bog'lanmagan muhitda (dev, bot token yo'q) bo'sh
	 * qoldiriladi va havola umuman ko'rsatilmaydi. `.transform` bo'sh/probel
	 * satrni `undefined` ga aylantiradi — xuddi `VITE_MAP_FALLBACK_URL`
	 * naqshi kabi: `${VITE_TELEGRAM_BOT_USERNAME:-}` bilan qurilgan image'da
	 * operator qiymat bermasa BO'SH SATR keladi, `undefined` EMAS.
	 *
	 * ⚠️ Bot username maxfiy EMAS — Telegram'da ochiq (kanal/bot qidiruvda
	 * ko'rinadi), shuning uchun uni build vaqtida bundle'ga yozish
	 * xavfsizlik muammosi emas (spec §7).
	 *
	 * ⚠️ `@` prefiksisiz saqlanadi: qiymat bevosita `t.me/<username>` ga
	 * qo'shiladi, ekranda esa `@` alohida chiziladi. Boshida `@` bilan
	 * kelsa (operator xatosi) UI qatlamida kesib tashlanadi.
	 */
	VITE_TELEGRAM_BOT_USERNAME: z
		.string()
		.optional()
		.transform((value) => (value && value.trim().length > 0 ? value.trim() : undefined)),
});

export type ClientEnv = z.infer<typeof clientEnvSchema>;

let cachedClientEnv: ClientEnv | null = null;

export function getClientEnv(): ClientEnv {
	if (cachedClientEnv) {
		return cachedClientEnv;
	}

	// Vite uses import.meta.env instead of process.env
	const envSource =
		typeof import.meta !== "undefined" && import.meta.env ? import.meta.env : process.env;

	const result = clientEnvSchema.safeParse(envSource);

	if (!result.success) {
		const formatted = z.prettifyError(result.error);
		throw new Error(`Invalid client environment variables:\n${formatted}`);
	}

	cachedClientEnv = result.data;
	return cachedClientEnv;
}

export { serverEnvSchema, clientEnvSchema };
